import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, CheckCircle, PaperPlaneRight } from "@phosphor-icons/react";
import { API_BASE } from "../lib/site";

/**
 * Registration modal for the two online support programs.
 * Purpose is intake only — no medical, prognosis, or clinical details are collected.
 *
 * Props:
 *   open (bool)             — controls visibility
 *   onClose ()              — request close (X, backdrop, Esc, "Close" after success)
 *   program                 — "hospice_family_support" | "bereavement_support"
 *   programLabel            — human-readable label
 */
const initial = (program) => ({
  program,
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  registering_for: "myself",
  preferred_contact: "email",
  message: "",
  consent: false,
});

const OnlineClassRegistrationDialog = ({ open, onClose, program, programLabel }) => {
  const [form, setForm] = useState(() => initial(program));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null); // holds the confirmation program label

  // Reset the form each time the dialog re-opens for a new program.
  useEffect(() => {
    if (open) {
      setForm(initial(program));
      setSuccess(null);
      setError("");
    }
  }, [open, program]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // Client validation
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      setError("Please complete the required fields.");
      return;
    }
    if (!form.consent) {
      setError("Please agree to be contacted to complete registration.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/class-registrations`, {
        program: form.program,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        registering_for: form.registering_for,
        preferred_contact: form.preferred_contact,
        message: form.message.trim() || null,
        consent: form.consent,
        source_page: typeof window !== "undefined" ? window.location.pathname : null,
      });
      setSuccess(programLabel);
      toast.success("Registration received.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "We couldn't submit your registration. Please try again.";
      setError(
        typeof detail === "string"
          ? detail
          : "We couldn't submit your registration. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reg-title"
      data-testid="class-registration-dialog"
    >
      <button
        type="button"
        aria-label="Close registration"
        onClick={onClose}
        className="absolute inset-0 bg-ink-900/50 backdrop-blur-sm"
        data-testid="class-registration-backdrop"
      />

      <div className="relative w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden rounded-t-2xl border border-ink-100 bg-white shadow-2xl sm:rounded-2xl">
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-ink-100 bg-gradient-to-br from-primary to-primary-dark px-5 py-4 text-white">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-secondary">
              Online Support Registration
            </div>
            <div
              id="reg-title"
              className="mt-0.5 font-heading text-lg font-medium leading-tight"
            >
              {programLabel}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="class-registration-close"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white/85 hover:bg-white/10 hover:text-white"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          {success ? (
            <div data-testid="class-registration-success" className="text-center py-4">
              <span className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CheckCircle size={28} weight="duotone" />
              </span>
              <h3 className="font-heading text-2xl text-ink-900 font-medium">
                You&rsquo;re Registered
              </h3>
              <p className="mt-3 text-ink-700 leading-relaxed">
                Thank you for registering for <strong>{success}</strong>.
              </p>
              <p className="mt-3 text-ink-700 leading-relaxed">
                We have received your registration. Begin Again Living Ministries
                will contact you with upcoming session dates, instructions for
                joining online, and any additional information you may need.
              </p>
              <p className="mt-3 text-ink-700 leading-relaxed">
                We look forward to supporting you.
              </p>
              <button
                type="button"
                onClick={onClose}
                data-testid="class-registration-done"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 font-semibold text-sm text-white hover:bg-primary-dark transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="class-registration-form">
              {/* Read-only program display */}
              <div className="rounded-xl bg-softblue/40 border border-primary/10 px-3 py-2">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">
                  Registering for
                </div>
                <div className="text-sm text-ink-800 font-medium mt-0.5">
                  {programLabel}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold text-ink-700">
                    First name <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    required
                    value={form.first_name}
                    onChange={(e) => setField("first_name", e.target.value)}
                    data-testid="reg-first-name"
                    className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-ink-700">
                    Last name <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="text"
                    required
                    value={form.last_name}
                    onChange={(e) => setField("last_name", e.target.value)}
                    data-testid="reg-last-name"
                    className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-xs font-semibold text-ink-700">
                  Email address <span className="text-red-500">*</span>
                </span>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  data-testid="reg-email"
                  className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
              </label>

              <label className="block">
                <span className="text-xs font-semibold text-ink-700">
                  Phone number <span className="text-ink-400">(optional)</span>
                </span>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  data-testid="reg-phone"
                  className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
              </label>

              <div>
                <span className="text-xs font-semibold text-ink-700">
                  Who are you registering for?
                </span>
                <div className="mt-1 flex flex-col gap-1.5">
                  {[
                    { v: "myself", l: "Myself" },
                    { v: "myself_and_family", l: "Myself and a family member / caregiver" },
                    { v: "family_member", l: "A family member" },
                  ].map((opt) => (
                    <label key={opt.v} className="inline-flex items-center gap-2 text-sm text-ink-800">
                      <input
                        type="radio"
                        name="registering_for"
                        value={opt.v}
                        checked={form.registering_for === opt.v}
                        onChange={() => setField("registering_for", opt.v)}
                        data-testid={`reg-for-${opt.v}`}
                        className="text-primary focus:ring-primary"
                      />
                      {opt.l}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <span className="text-xs font-semibold text-ink-700">
                  Preferred method of contact
                </span>
                <div className="mt-1 flex gap-4">
                  {[
                    { v: "email", l: "Email" },
                    { v: "phone", l: "Phone" },
                    { v: "text", l: "Text" },
                  ].map((opt) => (
                    <label key={opt.v} className="inline-flex items-center gap-2 text-sm text-ink-800">
                      <input
                        type="radio"
                        name="preferred_contact"
                        value={opt.v}
                        checked={form.preferred_contact === opt.v}
                        onChange={() => setField("preferred_contact", opt.v)}
                        data-testid={`reg-contact-${opt.v}`}
                        className="text-primary focus:ring-primary"
                      />
                      {opt.l}
                    </label>
                  ))}
                </div>
              </div>

              <label className="block">
                <span className="text-xs font-semibold text-ink-700">
                  Message or questions <span className="text-ink-400">(optional)</span>
                </span>
                <textarea
                  rows={3}
                  maxLength={2000}
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value)}
                  placeholder="Anything we should know? (Please do not include medical or health details.)"
                  data-testid="reg-message"
                  className="mt-1 block w-full resize-none rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
              </label>

              <label className="flex items-start gap-2.5 rounded-xl border border-ink-100 bg-softblue/20 p-3">
                <input
                  type="checkbox"
                  required
                  checked={form.consent}
                  onChange={(e) => setField("consent", e.target.checked)}
                  data-testid="reg-consent"
                  className="mt-0.5 text-primary focus:ring-primary"
                />
                <span className="text-xs leading-snug text-ink-700">
                  I agree to be contacted by Begin Again Living Ministries
                  regarding this online support program.
                </span>
              </label>

              {error && (
                <div
                  role="alert"
                  data-testid="reg-error"
                  className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
                >
                  {error}
                </div>
              )}

              <div className="pt-1">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="reg-submit"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-sm text-white hover:bg-primary-dark transition-colors disabled:opacity-60"
                >
                  <PaperPlaneRight size={14} weight="bold" />
                  {submitting ? "Submitting..." : "Complete Registration"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnlineClassRegistrationDialog;
